import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, X, CheckCircle2, XCircle, RotateCcw, Trophy } from "lucide-react";
import InteractiveMap from "@/components/InteractiveMap";
import DragToOrder from "@/components/DragToOrder";
import MatchPairs from "@/components/MatchPairs";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

/**
 * <GeographyGate lessonId tier childId classId onClose onPass /> —
 * mandatory quiz gate replacing the old "I learned it" self-report
 * on Geography lessons.
 *
 * Rules from spec §2:
 *   Seedlings 3 Qs · 100%   (3/3 pass)
 *   Explorers 5 Qs · 80%    (4/5)
 *   Pioneers  5 Qs · 80%    (4/5)
 *   Fresh set each retry, unlimited retries, no lock-out.
 */
export default function GeographyGate({ lessonId, tier, childId, classId, onClose, onPass }) {
  const [phase, setPhase] = useState("loading"); // loading | play | result
  const [session, setSession] = useState(null);
  const [answers, setAnswers] = useState({});   // question_id -> choice_index
  const [result, setResult] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [idx, setIdx] = useState(0);

  const startQuiz = async () => {
    setPhase("loading");
    setResult(null); setAnswers({}); setIdx(0);
    try {
      const r = await fetch(`${API}/geo-gate/start`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lesson_id: lessonId, tier, child_id: childId, class_id: classId }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setSession(j); setPhase("play");
    } catch (e) {
      toast.error(`Load failed: ${e.message}`);
      setPhase("play");
    }
  };
  useEffect(() => { startQuiz(); }, []); // eslint-disable-line

  const submit = async () => {
    setPhase("loading");
    setAttempts((a) => a + 1);
    try {
      const payload = session.questions.map((q) => {
        if (q.kind === "type-the-answer") {
          return { question_id: q.question_id, typed_answer: answers[q.question_id] || "" };
        }
        if (q.kind === "map-click") {
          return { question_id: q.question_id, clicked_iso3: answers[q.question_id] || "" };
        }
        if (q.kind === "drag-to-order") {
          return { question_id: q.question_id, ordered_iso3s: answers[q.question_id] || [] };
        }
        if (q.kind === "match-pairs") {
          return { question_id: q.question_id, pairs: answers[q.question_id] || {} };
        }
        return { question_id: q.question_id, choice_index: answers[q.question_id] ?? -1 };
      });
      const r = await fetch(`${API}/geo-gate/submit`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: session.session_id, lesson_id: lessonId, tier,
          child_id: childId, class_id: classId, answers: payload,
        }),
      });
      const j = await r.json();
      setResult(j);
      setPhase("result");
      if (j.passed && onPass) onPass(j);
    } catch (e) {
      toast.error(`Submit failed: ${e.message}`);
      setPhase("play");
    }
  };

  const answered = session?.questions?.filter((q) => {
    const v = answers[q.question_id];
    if (q.kind === "type-the-answer") return typeof v === "string" && v.trim().length > 0;
    if (q.kind === "map-click") return typeof v === "string" && v.length === 3;
    if (q.kind === "drag-to-order") return Array.isArray(v) && v.length === (q.items?.length || 0);
    if (q.kind === "match-pairs") return v && typeof v === "object" && Object.keys(v).length === (q.pairs_left?.length || 0);
    return v !== undefined;
  }).length ?? 0;
  const total = session?.questions?.length ?? 0;
  const canSubmit = answered === total && total > 0 && phase === "play";

  return (
    <div
      role="dialog" aria-modal="true"
      className="fixed inset-0 z-[110] flex items-center justify-center p-3 bg-slate-900/60 backdrop-blur"
      data-testid="geo-gate"
    >
      <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl border-4 border-slate-900 bg-white p-4 md:p-5 shadow-[8px_8px_0_0_rgba(15,23,42,1)]">
        <div className="flex items-center justify-between gap-2">
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-emerald-800">Show what you learned</p>
            <h2 className="font-heading text-xl md:text-2xl font-bold">Show what you know</h2>
          </div>
          <button
            type="button" onClick={onClose}
            data-testid="geo-gate-close"
            aria-label="Close and continue reading"
            className="inline-flex items-center justify-center h-11 w-11 rounded-full border-[3px] border-slate-900 bg-white shadow-[0_3px_0_0_rgba(15,23,42,1)]"
          >
            <X size={16} strokeWidth={3} />
          </button>
        </div>

        {phase === "loading" && (
          <p className="mt-4 inline-flex items-center gap-2 font-bold text-slate-600" data-testid="geo-gate-loading">
            <Loader2 className="animate-spin" size={16} strokeWidth={3} /> Building your quiz…
          </p>
        )}

        {phase === "play" && session && (
          <>
            <p className="mt-1 text-xs font-bold text-slate-600">
              {session.count} questions · pass at {session.pass_pct}%
              {attempts > 0 && ` · retry ${attempts}`}
            </p>

            {/* Progress dots */}
            <div className="mt-3 flex gap-1" data-testid="geo-gate-progress">
              {session.questions.map((q, i) => (
                <span key={q.question_id}
                  className={`h-2 flex-1 rounded-full border border-slate-900 ${
                    answers[q.question_id] !== undefined ? "bg-emerald-300" : (i === idx ? "bg-yellow-200" : "bg-white")
                  }`}
                />
              ))}
            </div>

            <Question
              q={session.questions[idx]}
              selected={answers[session.questions[idx].question_id]}
              onSelect={(ci) => setAnswers({ ...answers, [session.questions[idx].question_id]: ci })}
            />

            <div className="mt-3 flex gap-2 justify-between">
              <button
                type="button" disabled={idx === 0}
                onClick={() => setIdx((i) => Math.max(0, i - 1))}
                data-testid="geo-gate-prev"
                className="inline-flex items-center gap-1 h-11 px-3 rounded-full border-[3px] border-slate-900 bg-white font-bold text-sm shadow-[0_3px_0_0_rgba(15,23,42,1)] disabled:opacity-40"
              >
                ← Back
              </button>
              {idx < total - 1 ? (
                <button
                  type="button" onClick={() => setIdx((i) => Math.min(total - 1, i + 1))}
                  disabled={(() => {
                    const q = session.questions[idx];
                    const v = answers[q.question_id];
                    if (q.kind === "type-the-answer") return !(typeof v === "string" && v.trim().length > 0);
                    if (q.kind === "map-click") return !(typeof v === "string" && v.length === 3);
                    if (q.kind === "drag-to-order") return !(Array.isArray(v) && v.length === (q.items?.length || 0));
                    if (q.kind === "match-pairs") return !(v && typeof v === "object" && Object.keys(v).length === (q.pairs_left?.length || 0));
                    return v === undefined;
                  })()}
                  data-testid="geo-gate-next"
                  className="inline-flex items-center gap-1 h-11 px-4 rounded-full border-[3px] border-slate-900 bg-yellow-200 font-bold text-sm shadow-[0_3px_0_0_rgba(15,23,42,1)] disabled:opacity-40"
                >
                  Next →
                </button>
              ) : (
                <button
                  type="button" onClick={submit} disabled={!canSubmit}
                  data-testid="geo-gate-submit"
                  className="inline-flex items-center gap-1 h-11 px-4 rounded-full border-[3px] border-slate-900 bg-emerald-300 font-bold text-sm shadow-[0_3px_0_0_rgba(15,23,42,1)] disabled:opacity-40"
                >
                  Submit
                </button>
              )}
            </div>
          </>
        )}

        {phase === "result" && result && (
          <section className="mt-3" data-testid="geo-gate-result">
            <div className={`rounded-2xl border-[3px] border-slate-900 p-3 shadow-[3px_3px_0_0_rgba(15,23,42,1)] ${result.passed ? "bg-emerald-100" : "bg-amber-100"}`}>
              <div className="flex items-center gap-2">
                {result.passed ? <Trophy size={20} strokeWidth={3} /> : <RotateCcw size={20} strokeWidth={3} />}
                <h3 className="font-heading text-lg font-bold" data-testid="geo-gate-verdict">
                  {result.passed ? "You showed what you learned!" : "Nearly there — try again"}
                </h3>
              </div>
              <p className="mt-1 text-sm font-bold text-slate-700">
                {result.score}/{result.total} correct · {result.pct}%
              </p>
              {!result.passed && result.failed_tags?.length > 0 && (
                <p className="mt-2 text-xs font-bold text-slate-700" data-testid="geo-gate-review">
                  Review these areas: {result.failed_tags.slice(0, 3).map((t) => t.split(".").pop()).join(", ")}
                </p>
              )}
            </div>
            <div className="mt-3 flex gap-2 flex-wrap">
              {result.passed ? (
                <button
                  type="button" onClick={onClose}
                  data-testid="geo-gate-continue"
                  className="inline-flex items-center gap-1 h-11 px-4 rounded-full border-[3px] border-slate-900 bg-emerald-300 font-bold text-sm shadow-[0_3px_0_0_rgba(15,23,42,1)]"
                >
                  Continue →
                </button>
              ) : (
                <button
                  type="button" onClick={startQuiz}
                  data-testid="geo-gate-retry"
                  className="inline-flex items-center gap-1 h-11 px-4 rounded-full border-[3px] border-slate-900 bg-yellow-300 font-bold text-sm shadow-[0_3px_0_0_rgba(15,23,42,1)]"
                >
                  <RotateCcw size={14} strokeWidth={3} /> Try a fresh set
                </button>
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function Question({ q, selected, onSelect }) {
  if (q.kind === "type-the-answer") {
    return (
      <div className="mt-3 rounded-2xl border-[3px] border-slate-900 bg-yellow-50 p-3 shadow-[3px_3px_0_0_rgba(15,23,42,1)]" data-testid="geo-gate-question">
        <p className="text-[10px] font-black uppercase tracking-widest text-slate-600">type the answer</p>
        <p className="mt-1 font-heading text-base md:text-lg font-bold whitespace-pre-line">{q.stem}</p>
        <input
          type="text"
          value={selected || ""}
          onChange={(e) => onSelect(e.target.value)}
          placeholder="Type here…"
          data-testid="geo-gate-type-input"
          autoComplete="off"
          spellCheck={false}
          className="mt-2 w-full h-12 px-3 rounded-full border-[3px] border-slate-900 bg-white font-bold text-base shadow-[0_3px_0_0_rgba(15,23,42,1)] outline-none focus:ring-2 focus:ring-emerald-400"
        />
        <p className="mt-1 text-[11px] font-bold text-slate-500 italic">Spelling is forgiving — small typos are OK.</p>
      </div>
    );
  }
  if (q.kind === "map-click") {
    return (
      <div className="mt-3 rounded-2xl border-[3px] border-slate-900 bg-yellow-50 p-3 shadow-[3px_3px_0_0_rgba(15,23,42,1)]" data-testid="geo-gate-question">
        <p className="text-[10px] font-black uppercase tracking-widest text-slate-600">tap on the map</p>
        <p className="mt-1 font-heading text-base md:text-lg font-bold whitespace-pre-line">{q.stem}</p>
        <div className="mt-2" data-testid="geo-gate-map-click">
          <InteractiveMap
            mode="click"
            focusContinent={q.map_hint_continent}
            selectedIso3={selected || null}
            onSelect={(iso3) => onSelect(iso3)}
            height={280}
            testIdPrefix="geo-gate-map"
          />
        </div>
        <p className="mt-1 text-[11px] font-bold text-slate-500 italic">
          {selected ? `Selected: ${selected}` : "Tip: pinch or scroll to zoom for tiny countries."}
        </p>
      </div>
    );
  }
  if (q.kind === "reverse-map-click") {
    return (
      <div className="mt-3 rounded-2xl border-[3px] border-slate-900 bg-yellow-50 p-3 shadow-[3px_3px_0_0_rgba(15,23,42,1)]" data-testid="geo-gate-question">
        <p className="text-[10px] font-black uppercase tracking-widest text-slate-600">which country?</p>
        <p className="mt-1 font-heading text-base md:text-lg font-bold whitespace-pre-line">{q.stem}</p>
        <div className="mt-2" data-testid="geo-gate-map-highlight">
          <InteractiveMap
            mode="highlight"
            highlightIso3={q.map_highlight_iso3}
            height={220}
            testIdPrefix="geo-gate-map"
          />
        </div>
        <div className="mt-2 grid grid-cols-1 gap-2">
          {(q.choices || []).map((choice, i) => (
            <button
              key={i} type="button" onClick={() => onSelect(i)}
              data-testid={`geo-gate-choice-${i}`}
              className={`text-left min-h-11 px-3 rounded-2xl border-[3px] border-slate-900 font-bold text-sm shadow-[0_3px_0_0_rgba(15,23,42,1)] active:translate-y-[2px] active:shadow-none ${
                selected === i ? "bg-emerald-200" : "bg-white"
              }`}
            >
              {choice}
            </button>
          ))}
        </div>
      </div>
    );
  }
  if (q.kind === "drag-to-order") {
    return (
      <div className="mt-3 rounded-2xl border-[3px] border-slate-900 bg-yellow-50 p-3 shadow-[3px_3px_0_0_rgba(15,23,42,1)]" data-testid="geo-gate-question">
        <p className="text-[10px] font-black uppercase tracking-widest text-slate-600">drag into order</p>
        <p className="mt-1 font-heading text-base md:text-lg font-bold whitespace-pre-line">{q.stem}</p>
        <div className="mt-2" data-testid="geo-gate-drag-order">
          <DragToOrder
            items={q.items || []}
            value={selected}
            onChange={onSelect}
            testIdPrefix="geo-gate-dto"
          />
        </div>
      </div>
    );
  }
  if (q.kind === "match-pairs") {
    return (
      <div className="mt-3 rounded-2xl border-[3px] border-slate-900 bg-yellow-50 p-3 shadow-[3px_3px_0_0_rgba(15,23,42,1)]" data-testid="geo-gate-question">
        <p className="text-[10px] font-black uppercase tracking-widest text-slate-600">match the pairs</p>
        <p className="mt-1 font-heading text-base md:text-lg font-bold whitespace-pre-line">{q.stem}</p>
        <div className="mt-2" data-testid="geo-gate-match-pairs">
          <MatchPairs
            pairs_left={q.pairs_left || []}
            pairs_right={q.pairs_right || []}
            value={selected}
            onChange={onSelect}
            testIdPrefix="geo-gate-mp"
          />
        </div>
        <p className="mt-1 text-[11px] font-bold text-slate-500 italic">
          Tap a country, then tap its capital. Tap again to undo.
        </p>
      </div>
    );
  }
  return (
    <div className="mt-3 rounded-2xl border-[3px] border-slate-900 bg-yellow-50 p-3 shadow-[3px_3px_0_0_rgba(15,23,42,1)]" data-testid="geo-gate-question">
      <p className="text-[10px] font-black uppercase tracking-widest text-slate-600">{q.kind.replace(/-/g, " ")}</p>
      <p className="mt-1 font-heading text-base md:text-lg font-bold whitespace-pre-line">{q.stem}</p>
      {q.flag_emoji && (
        <div className="mt-2 text-6xl" aria-label="Country flag" data-testid="geo-gate-flag">{q.flag_emoji}</div>
      )}
      <div className="mt-2 grid grid-cols-1 gap-2">
        {(q.choices || []).map((choice, i) => (
          <button
            key={i} type="button" onClick={() => onSelect(i)}
            data-testid={`geo-gate-choice-${i}`}
            className={`text-left min-h-11 px-3 rounded-2xl border-[3px] border-slate-900 font-bold text-sm shadow-[0_3px_0_0_rgba(15,23,42,1)] active:translate-y-[2px] active:shadow-none ${
              selected === i ? "bg-emerald-200" : "bg-white"
            }`}
          >
            {choice}
          </button>
        ))}
      </div>
    </div>
  );
}
